import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../lib/api-client';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Input } from '../ui/Input';
import { Skeleton } from '../ui/Skeleton';
import { useCacheEvents } from '../../hooks/useCacheEvents';
import { cn } from '../../lib/utils';
import { ChevronDown, ChevronUp, Send, MessageSquare, CheckCircle2, Trash2 } from 'lucide-react';
import { useArchivePanel, ArchiveHeader } from './ArchivePanel';

interface HumanInputItem {
  id: string;
  prompt: string;
  answer?: string;
  answeredBy?: string;
  answeredAt?: string;
}

interface HumanInput {
  id: string;
  runId: string | null;
  streamId: string;
  contextId: string;
  type: string;
  status: string;
  requestedByStep: string;
  requestedAt: string;
  resolvedAt?: string;
  title: string;
  items: HumanInputItem[];
  triggeredBy?: string;
  source?: string;
}

interface HumanInputsTabProps {
  streamId: string;
  canIntervene: boolean;
  onDeleteInput?: (contextId: string) => void;
}

function statusVariant(status: string): 'default' | 'success' | 'destructive' | 'outline' {
  if (status === 'answered' || status === 'approved' || status === 'completed') return 'success';
  if (status === 'rejected' || status === 'expired') return 'destructive';
  if (status === 'active') return 'default';
  return 'outline';
}

export function HumanInputsTab({ streamId, canIntervene, onDeleteInput }: HumanInputsTabProps) {
  const [inputs, setInputs] = useState<HumanInput[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [answers, setAnswers] = useState<Record<string, Record<string, string>>>({});
  const [submitting, setSubmitting] = useState<Set<string>>(new Set());
  const [archiveInputs, setArchiveInputs] = useState<HumanInput[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (streamId) params.set('streamId', streamId);
      const qs = params.toString();
      const path = qs ? `/api/ai-workflows/human-inputs?${qs}` : '/api/ai-workflows/human-inputs';
      const data = await apiClient.get<{ inputs: HumanInput[] }>(path);
      setInputs(data.inputs || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load human inputs');
      setInputs([]);
    } finally {
      setLoading(false);
    }
  }, [streamId]);

  useEffect(() => { void load(); }, [load]);
  useCacheEvents({ cacheNames: ['ai-workflow-runs'], onEvent: () => { void load(); } });

  const archivePanel = useArchivePanel({
    onSearch: async (from, to) => {
      const result = await apiClient.getAiHumanInputsArchive({
        from,
        to,
        streamId: streamId || undefined,
      });
      setArchiveInputs(result.inputs || []);
    },
  });

  const displayInputs = archivePanel.archiveMode ? archiveInputs : inputs;

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const setAnswer = (contextId: string, questionId: string, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [contextId]: { ...prev[contextId], [questionId]: value },
    }));
  };

  const submitAnswer = async (contextId: string, questionId: string) => {
    const answer = answers[contextId]?.[questionId]?.trim();
    if (!answer) return;
    const key = `${contextId}:${questionId}`;
    setSubmitting((prev) => new Set(prev).add(key));
    try {
      await apiClient.post(`/api/ai-workflows/human-inputs/${contextId}/respond`, {
        questionId,
        answer,
      });
      setAnswers((prev) => {
        const updated = { ...prev };
        if (updated[contextId]) delete updated[contextId][questionId];
        return updated;
      });
      await load();
    } catch (e) {
      console.error('Failed to submit answer:', e);
    } finally {
      setSubmitting((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const activeInputs = displayInputs.filter((i) => i.status === 'active');
  const pastInputs = displayInputs.filter((i) => i.status !== 'active');

  if (loading) {
    return (
      <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
        <Skeleton className="h-32 rounded-lg" />
        <Skeleton className="h-32 rounded-lg" />
        <Skeleton className="h-32 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
        {error}
      </div>
    );
  }

  if (displayInputs.length === 0) {
    return (
      <div className="space-y-4">
        <ArchiveHeader label="Human Inputs" count={0} panel={archivePanel} />
        <Card className="border-dashed border-gray-300 dark:border-gray-600">
          <CardContent className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            {archivePanel.archiveMode ? 'No human input requests found in this date range.' : 'No human input requests.'}
          </CardContent>
        </Card>
      </div>
    );
  }

  const renderCard = (input: HumanInput) => {
    const isActive = input.status === 'active';
    const isExpanded = expandedIds.has(input.id);
    const unanswered = input.items.filter((q) => !q.answer);
    const answered = input.items.filter((q) => q.answer);

    return (
      <Card key={input.id} className={cn(
        'border-gray-200 dark:border-gray-700',
        isActive && 'border-blue-300 dark:border-blue-700',
      )}>
        <CardHeader className="px-3 py-2">
          <div className="flex items-start justify-between gap-1.5">
            <div className="min-w-0 flex-1">
              <CardTitle className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                {input.title}
              </CardTitle>
              <p className="truncate text-[10px] text-gray-400 dark:text-gray-500">
                {input.type.replace(/_/g, ' ')} · {input.requestedByStep} · {input.triggeredBy || ''}
              </p>
            </div>
            <div className="flex items-center gap-1.5">
              <Badge variant={statusVariant(input.status)} className="text-[10px]">
                {input.status}
              </Badge>
              {isActive && <MessageSquare className="h-3.5 w-3.5 text-blue-500" />}
              {!isActive && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 px-3 pb-2.5 pt-0">
          <div className="text-[10px] text-gray-500 dark:text-gray-400">
            {answered.length}/{input.items.length} answered
            {input.requestedAt && ` · ${new Date(input.requestedAt).toLocaleDateString()}`}
          </div>

          {isActive && canIntervene && unanswered.map((q) => (
            <div key={q.id} className="space-y-1">
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{q.prompt}</p>
              <div className="flex gap-1.5">
                <Input
                  className="h-7 text-xs"
                  placeholder="Your answer..."
                  value={answers[input.contextId]?.[q.id] || ''}
                  onChange={(e) => setAnswer(input.contextId, q.id, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitAnswer(input.contextId, q.id);
                  }}
                />
                <Button
                  size="sm"
                  className="h-7 w-7 shrink-0 p-0"
                  disabled={submitting.has(`${input.contextId}:${q.id}`) || !answers[input.contextId]?.[q.id]?.trim()}
                  onClick={() => submitAnswer(input.contextId, q.id)}
                >
                  <Send className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}

          {!isActive && onDeleteInput && (
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="ghost"
                className="h-6 gap-1 px-2 text-[10px] text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400"
                onClick={() => onDeleteInput(input.contextId)}
              >
                <Trash2 className="h-3 w-3" />
                Delete
              </Button>
            </div>
          )}
          {!isActive && input.items.length > 0 && (
            <button
              type="button"
              onClick={() => toggleExpand(input.id)}
              className="flex items-center gap-1 text-[10px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {isExpanded ? 'Hide' : 'Show'} Q&A
            </button>
          )}

          {isExpanded && input.items.map((q) => (
            <div key={q.id} className="rounded border border-gray-100 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800/50">
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{q.prompt}</p>
              {q.answer ? (
                <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                  {q.answer}
                  {q.answeredBy && <span className="ml-1 text-gray-400">— {q.answeredBy}</span>}
                </p>
              ) : (
                <p className="mt-0.5 text-xs italic text-gray-400">Unanswered</p>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      <ArchiveHeader label="Human Inputs" count={displayInputs.length} panel={archivePanel} />
      {activeInputs.length > 0 && (
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Active</h3>
          <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
            {activeInputs.map(renderCard)}
          </div>
        </div>
      )}
      {pastInputs.length > 0 && (
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Past</h3>
          <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
            {pastInputs.map(renderCard)}
          </div>
        </div>
      )}
    </div>
  );
}
